/**
 * Jovii — SHOULD THE INTRO PLAY?
 *
 * Netflix plays its ident on a COLD start: the first open, and every open after
 * the app was no longer loaded. It does NOT play it again while the app is still
 * running. This file is that rule, written down:
 *
 *   - on a cold start at the app root — a fresh process or a fresh tab, which is
 *     to say the app was not already loaded — tracked in `sessionStorage`;
 *   - never twice in one session: an in-session navigation back to "/" and a
 *     reload both keep the flag, because the app never went away;
 *   - never on a deep link, because someone opening a shared film or a room
 *     invitation asked for that thing, not for our logo;
 *   - never when the tab was restored from the back/forward cache, which is a
 *     warm start wearing a cold start's clothes.
 *
 * ── WHY `sessionStorage` AND NOT `localStorage` ────────────────────────────────
 *
 * It used to be `localStorage`, which made the rule "once per browser, for ever".
 * The owner's report was exact: *"on iPhone and iPad the intro only ever plays
 * once."* That is what `localStorage` means — it outlives the process, the app,
 * and the reinstall of nothing at all. `sessionStorage` is scoped to the
 * **browsing-context session**: it is created when a top-level browsing context
 * is created and destroyed when it is (HTML standard, "session storage area"), so
 * it dies with the tab, with a Home Screen web app's process (`com.apple.webapp`)
 * and with the native WKWebView shell's process — and that is precisely the
 * definition of "the app wasn't loaded". It SURVIVES a reload and an in-session
 * navigation, which is precisely the set of things that must not replay it.
 *
 * `INTRO_KEY` is still the stored VALUE, not just a marker, so bumping it replays
 * the intro for everyone mid-session and busts the glyph cache at the same time —
 * the asset URLs in `Intro.svelte` carry the same value.
 *
 * ── THE ONE PLACE THIS CAN STILL LOOK WRONG, AND IT IS NOT THIS FILE ───────────
 *
 * DEPENDENCY ON THE iOS SWIPE-AWAY DEFECT (docs/OPEN-DEFECTS.md § B2). "A new
 * session" means "a new process". If the iOS process does NOT actually die when
 * the user swipes the card away in the app switcher — and an open
 * Picture-in-Picture window is exactly a mechanism that keeps the WebContent
 * process alive and foreground-asserted past the swipe, because
 * `PageClientImpl::isActiveViewVisible()` returns true while PiP is showing —
 * then the browsing-context session survives too, `sessionStorage` still holds
 * the flag, and the intro will still not replay. The two defects share a root:
 * "the app went away" is not something iOS tells us, it is something we have to
 * stop preventing. B2 removes the mechanism that was preventing it for solo
 * playback; this file is correct once the process is allowed to die.
 *
 * A deliberately restored session (Safari's "reopen tabs from last time") also
 * restores `sessionStorage` and will not replay the intro. That is the correct
 * answer for a restored tab and it is the same answer any cold-start signal
 * built on the page would give.
 */
export const INTRO_KEY = 'jovi-2';

/**
 * The session flag. The key name is unchanged from the `localStorage` era on
 * purpose: it is a different storage AREA, so there is no collision, and keeping
 * the name means the support answer ("clear `jt.intro.v1`") still reads the same.
 */
const STORAGE_KEY = 'jt.intro.v1';

/**
 * The dead `localStorage` entry from when this was once-per-browser. Cleared on
 * the way past so a device that saw the intro in September is not carrying a
 * permanent tombstone for a rule that no longer exists.
 */
const LEGACY_KEY = 'jt.intro.v1';

/** Paths that must never be delayed by an animation. */
function isDeepLink(): boolean {
  if (typeof location === 'undefined') return false;
  const path = location.pathname.replace(/\/+$/, '');
  if (path !== '' && path !== '/') return true;
  /* A room invitation arrives in the fragment and is handled before any shell
     mounts; an intro in front of it would delay a person joining a live room. */
  return location.hash.includes('roomInvite');
}

/**
 * True once per SESSION, on a cold start at the app root.
 *
 * Reads but does NOT write: the flag is set by `markIntroSeen()` when the
 * animation actually runs, so a session that never got to see it (storage
 * cleared, a crash mid-boot) still gets its turn on the next one.
 */
export function shouldPlayIntro(): boolean {
  if (typeof window === 'undefined') return false;
  if (isDeepLink()) return false;

  /* A bfcache restore is not a cold start. */
  const nav = performance.getEntriesByType?.('navigation')?.[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (nav?.type === 'back_forward') return false;

  try {
    return sessionStorage.getItem(STORAGE_KEY) !== INTRO_KEY;
  } catch {
    /* Private mode: play it. Once per session is better than never, and the
       alternative is a permanent intro for anyone with storage disabled —
       which is the same bug in the other direction, but louder. */
    return true;
  }
}

/** Remember that this SESSION has seen the current intro. */
export function markIntroSeen(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, INTRO_KEY);
  } catch {
    /* Private mode; it plays again on the next navigation and that is
       acceptable. Nothing else in the boot depends on this write. */
  }
  try {
    /* Best effort, and deliberately here rather than in the read path: a
       question about whether to draw something must not write to storage. */
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* nothing to clear, or no localStorage at all */
  }
}

/** Test and support hook: make the next boot play the intro again. */
export function resetIntro(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
